import clone from 'lodash/clone';
import isString from 'lodash/isString';
import Base from './Base';

const _ = {
  clone,
  isString,
};

const validateThese = ['pattern', 'maxlength', 'minlength', 'maxListSize', 'minListSize', 'required'];

/**
 * Class to hold basic validation methods that can technically apply to
 * a wide range of data types. Usually extends a class of a more specific type
 * so that validation can be appropriately extended.
 */
class Field extends Base {
  /**
   * Initializes a new field object with config and value.
   *
   * @param {Object} config - A description of the field's constraints.
   * @param {Mixed} value - The value for the given field.
   */
  constructor(config = {}, value = null) {
    super();

    this.errors = [];
    this.originalValue = null;
    this.validateThese = validateThese;
    this.sanitized = false;
    this.run = false;
    this.checks = {};
    this.timestamp = null;
    this.valid = true;

    // Member of these groups
    this.groups = [];

    // Booleans
    this.validIndex = [];

    // Strings
    this.errorIndex = [];

    this.load(config, value);
  }

  /**
   * Loads a new config and value.  Data is converted into fields with
   * the appropriate type classes.
   *
   * @param {Object} config - A description of the field's constraints.
   * @param {Mixed} value - The value for the given field.
   * @return {Field}
   */
  load(config = {}, value) {
    this.config = config;

    // This is so you can load a new config without modifying the value
    if (value === undefined) {
      return this;
    }

    if (this.value === undefined) {
      this.originalValue = _.clone(value);
    }

    if (!Array.isArray(value)) {
      this.value = [_.clone(value)];
      this.displayValue = [_.clone(value)];
    } else {
      this.value = _.clone(value);
      this.displayValue = _.clone(value);

      // Auto-set collection
      this.config.collection = true;
    }

    ['maxlength', 'minlength'].forEach(item => {
      if (!Field.isEmpty(this.config[item])) {
        this.config[item] = parseInt(this.config[item], 10);
      }
    });

    return this;
  }

  getId() {
    return this.config.fieldName;
  }

  addGroup(group) {
    this.groups.push(group);
  }

  /**
   * Setter for the value of the field.
   * @param {Mixed} value - The value for the given field.
   * @param {Number} index - The index for the given value
   * @return {Field}
   */
  setValue(value, index) {
    // Auto setting of collection flag
    if (!this.config.collection && Array.isArray(value)) {
      this.config.collection = true;
    }

    if (Array.isArray(value) && index === undefined) {
      this.value = _.clone(value);
      return this;
    }

    if (index === undefined) {
      index = 0;
    }

    this.value[index] = _.clone(value);
    return this;
  }

  /**
   * Setter for the displayValue of the field.
   * @param {Mixed} value - The value for the given field.
   * @param {Number} index - The index for the give value.
   * @return {Field}
   */
  setDisplayValue(value, index = 0) {
    // Auto setting of collection flag
    if (!this.config.collection && Array.isArray(value)) {
      this.config.collection = true;
    }

    if (Array.isArray(value) && index === undefined) {
      this.displayValue = _.clone(value);
      return this;
    }

    if (index === undefined) {
      index = 0;
    }

    this.displayValue[index] = _.clone(value);
    return this;
  }

  /**
   * Getter for the display value of the field.
   *
   * @param {Number} index - The index for the give value.
   * @return {Mixed} - Typically an array of values if it's a collection,
   * otherwise a scalar value.
   */
  getDisplayValue(index) {
    if (this.config.collection && index === undefined) {
      return this.displayValue;
    }

    if (!this.config.collection || index === undefined) {
      index = 0;
    }

    return this.displayValue[index];
  }

  /**
   * Getter for the raw value of the field.
   *
   * @return {Mixed} - Typically an array of values if it's a collection,
   * otherwise a scalar value.
   */
  getValue(index) {
    // When this field is a collection and an index is not provided
    // this will return the entire collection (Array) back
    if (this.config.collection && index === undefined) {
      return this.value;
    }

    if (!this.config.collection || index === undefined) {
      index = 0;
    }

    return this.value[index];
  }

  /**
   * Getter for size of the data.
   * @return {Number}
   */
  getDataSize() {
    if (this.config.collection) {
      return this.value.length;
    }

    return 1;
  }

  /**
   * Getter for field dependencies.
   * @return {Object}
   */
  getDependencies() {
    return this.config.dependencies;
  }

  /**
   * Validates the maximum length of the value on the field.
   * @return {boolean}
   */
  minlength() {
    let valid = true;

    if (this.config.minlength) {
      this.value.forEach((val, index) => {
        if (Field.isEmpty(val) && !this.isRequired(index)) {
          return;
        }
        if (String(val || '').length < this.config.minlength) {
          this.invalidate('Minimum length not reached.', index);
          valid = false;
        }
        this.checks.minlength = true;
      });
    }

    return valid;
  }

  /**
   * Validates the maximum length of the value on the field.
   * @return {boolean}
   */
  maxlength() {
    let valid = true;

    if (this.config.maxlength) {
      this.value.forEach((val, index) => {
        if (!Field.isEmpty(val) && val.length > this.config.maxlength) {
          this.invalidate('Maxlength exceeded.', index);
          valid = false;
        }
        this.checks.maxlength = true;
      });
    }

    return valid;
  }

  /**
   * Validates the maximum list size of the value on the field.
   * @return {boolean}
   */
  maxListSize() {
    let valid = true;

    if (this.config.maxListSize) {
      if (this.value.length > this.config.maxListSize) {
        this.invalidate(`Maximum list size (${this.config.maxListSize}) exceeded.`);
        valid = false;
      }
      this.checks.maxListSize = true;
    }

    return valid;
  }

  /**
   * Validates the maximum list size of the value on the field.
   * @return {boolean}
   */
  minListSize() {
    let valid = true;

    if (this.config.minListSize) {
      if (this.value.length < this.config.minListSize) {
        this.invalidate(`Minimum list size (${this.config.minListSize}) not met.`);
        valid = false;
      }
      this.checks.minListSize = true;
    }

    return valid;
  }

  /**
   * Validates the minimum length of the value on the field.
   * @return {boolean}
   */
  required() {
    let valid = true;

    this.value.forEach((val, index) => {
      if (this.isRequired(index)) {
        if (Field.isEmpty(val)) {
          this.invalidate('Required.', index);
          valid = false;
        }
        this.checks.required = true;
      }
    });

    return valid;
  }

  /**
   * Matches the configured pattern for the field.
   * Acceptable patterns are:
   *   /^\d*$/ (RegExp prototype)
   *   "^\d*$" (String)
   *   "/^\d*$/" (String with boundaries)
   * @return {boolean}
   */
  pattern() {
    let valid = true;

    if (this.config.pattern) {
      let re = this.config.pattern;

      // Create from string
      if (_.isString(re)) {
        if (/^\/.*\/$/.test(re)) {
          re = new RegExp(re.slice(1, -1));
        } else {
          re = new RegExp(this.config.pattern);
        }
      }

      // Check for regex prototype
      if (!re.exec) {
        console.warn('Invalid pattern', this.config.pattern);
        return valid;
      }

      this.value.forEach((val, index) => {
        if (Field.isEmpty(val) && !this.isRequired(index)) {
          return;
        }

        if (!re.test(val)) {
          this.invalidate('Pattern mismatched.', index);
          valid = false;
        }
        this.checks.pattern = true;
      });
    }

    return valid;
  }

  /**
   * Parent method for sanitation.  We won't want to sanitize invalid data.
   * @return {Field}
   */
  sanitize(force = false) {
    if (!force && (!this.isCollectionValid() || !this.run)) {
      throw new Error(
        `Field '${this.getId()}': A successful validation must occur before sanitation. (Valid: ${this.valid}, Run: ${
          this.run
        })`
      );
    }

    this.sanitized = true;
    return this;
  }

  /**
   * Validates all the constraints defined by the configuration.
   * @return {boolean}
   */
  validate() {
    this.valid = true;
    this.errors = [];

    this.value.forEach((val, index) => {
      this.validIndex[index] = true;
      this.errorIndex[index] = null;
    }, this);

    this.validateThese.forEach(method => {
      if (this[method]) {
        this[method]();
      }
    }, this);

    this.run = true;
    this.timestamp = new Date();
    return this.isValid();
  }

  /**
   * Returns the results of any validation run on the field.
   * @return {Object}
   */
  report() {
    return {
      config: this.config,
      valid: this.valid,
      validIndex: this.validIndex,
      errorIndex: this.errorIndex,
      value: this.getValue(),
      displayValue: this.getDisplayValue(),
      run: this.run,
      timestamp: this.timestamp,
      errors: this.errors,
      sanitized: this.sanitized,
      originalValue: this.originalValue,
      checks: this.checks,
      dependencyActions: this.dependencyActions,
      dependencyIndex: this.dependencyIndex,
    };
  }

  /**
   * Sets the field's validity to false.
   * @return {Field}
   */
  invalidate(reason, index) {
    this.errors.push({
      reason,
      index,
    });

    this.valid = false;

    if (!Field.isEmpty(index)) {
      this.validIndex[index] = false;
      const currentError = this.errorIndex[index] ? `${this.errorIndex[index]} ` : '';
      this.errorIndex[index] = `${currentError}${reason}`;
    }

    return this;
  }

  needsValidation(index = 0) {
    if (Field.isEmpty(this.value[index]) && !this.isRequired(index)) {
      return false;
    }

    return true;
  }

  /**
   * Returns the current validity of the field's value.
   * @return {boolean}
   */
  isValid() {
    if (this.config.displayOnly === true) {
      return true;
    }

    return this.valid;
  }

  /**
   * Checks to see if the value is required to be validated
   * @param {Number} index - The index for the given value
   * @return {Boolean}
   */
  isRequired(index = 0) {
    const required = this.config.required || this.config.minlength === 1;

    // First check config's base requirement
    if (!required) {
      return false;
    }

    // Setting minLen to 1 is a hack to make a field required ...
    // TODO: consider phasing out
    if (this.config.minlength === 1 && !this.config.required) {
      console.warn(
        `The field ${this.getId()} has a minlength of 1 but is not set to required.  Consider using only required.`
      );
    }

    if (this.isHidden(index)) {
      return false;
    }

    return required;
  }

  /**
   * Checks to see if field is hidden
   * @param {Number} index - The index for the given value
   * @return {Boolean}
   */
  isHidden(index) {
    // Check if any groups are hidden/disabled
    if (this.groups.length) {
      for (let gi = 0; gi < this.groups.length; gi++) {
        if (this.groups[gi].isHidden()) {
          return true;
        }
      }
    }

    if (
      this.dependencyActions[index] &&
      (this.dependencyActions[index].includes('hide') || this.dependencyActions[index].includes('disable'))
    ) {
      return true;
    }

    return false;
  }

  /*
   * Returns how many in a collection are valid.
   * @return {Number} - The number valid.
   */
  isCollectionValid() {
    let valid = 0;

    if (!this.run) {
      console.warn(`Validation must be run before calling isCollectionValid for: ${this.getId()}`);
      return valid;
    }

    this.validIndex.forEach(index => {
      if (index) {
        valid += 1;
      }
    });

    return valid;
  }

  /**
   * Checks to see if the value is blank, 0 is a good value.
   * @param {Mixed} value - Any value
   * @return {Boolean}
   */
  static isEmpty(value) {
    if (value === undefined || value === null || value === '') {
      return true;
    }

    return false;
  }

  static commaize(value = '', precision) {
    if (value === '') {
      return value;
    }

    let newValue = parseFloat(value);

    let parts;
    if (precision !== undefined && precision !== null) {
      parts = newValue.toFixed(precision).split('.');
    } else {
      parts = String(newValue).split('.');
    }
    newValue = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,') + (parts[1] ? `.${parts[1]}` : '');
    return newValue;
  }
}

export default Field;
